<template>
  <div class="left-sidebar pa-6 d-flex flex-column ga-4">
    <template v-if="!isChromeView">
      <TemplateLink
        v-if="authData.nextUiEnabled && !isTemplate && jobProfile?.last_applied_template_ulid"
      />

      <ProfileLink
        v-if="jobProfile?.hris_reference"
        :profile="jobProfile.hris_reference"
        :is-hris-profile="true"
      />

      <ProfileLink
        v-for="draft in visibleDraftProfiles"
        :key="draft.ulid"
        :profile="draft"
      />

      <div
        v-if="hiddenDraftCount > 0 || draftsExpanded"
        class="text-body-2 text-textLight cursor-pointer"
        @click="draftsExpanded = !draftsExpanded"
      >
        <v-icon
          size="16"
          class="mr-1"
        >
          {{ draftsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-right' }}
        </v-icon>

        {{ draftsExpanded
          ? 'Show fewer draft profiles'
          : `and ${hiddenDraftCount} more draft ${hiddenDraftCount === 1 ? 'profile' : 'profiles'}`
        }}
      </div>
    </template>

    <template v-if="hasEditAccess || checkRbacPermissions(['linkDraftProfileHrisReference'])">
      <HrisProfileLinkCandidates
        v-if="
          !isHrisProfile
            && !isTemplate
            && !jobProfile?.hris_reference
            && !isChromeView"
      />
    </template>
    <GuidanceList
      :body-messages="profileGuidanceMessages"
      :is-revision-saving="false"
      :hide-actions="true"
      :hide-categories="['brand', 'performance']"
    >
      <template #custom-action="{ message }">
        <div v-if="authData.aiEnabled && message.action === 'custom_summary'">
          <FirstDraftButton
            small
            variant="plain"
            :disabled="readOnly"
            @click="generateSummary"
          >
            Generate Job Summary
          </FirstDraftButton>
        </div>

        <div v-if="message.id === 'guidance_template'">
          <template v-if="hasEditAccess || checkRbacPermissions(['applyProfileTemplate'])">
            <v-btn
              class="mt-2"
              variant="outlined"
              rounded="lg"
              color="primary"
              size="small"
              :disabled="readOnly"
              @click="setModal({ field: 'applyJobProfileTemplate', value: true })"
            >
              Apply Template
            </v-btn>
          </template>
        </div>
      </template>

      <template
        v-if="authData.aiEnabled && !isHrisProfile"
        #append-icon="{ message }"
      >
        <template
          v-for="generatorType in [
            'responsibilities',
            'skills',
            'experience',
            'competencies',
            'education',
            'certifications',
          ]"
          :key="generatorType"
        >
          <FirstDraftButton
            v-if="message.action === `custom_${generatorType}`"
            :disabled="rescorePending || readOnly"
            small
            variant="plain"
            width="30px"
            min-width="30px"
            @click="generateButtonClick(generatorType)"
          />
        </template>
      </template>
    </GuidanceList>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useModals } from '@/stores/modals/modals';
import { useAppRoute } from '@/stores/app/route';
import TemplateLink from '@/components/jobProfiles/editor/TemplateLink.vue';
import ProfileLink from '@/components/jobProfiles/editor/ProfileLink.vue';
import HrisProfileLinkCandidates from '@/components/jobProfiles/editor/HrisProfileLinkCandidates.vue';
import GuidanceList from '@ui/components/editor/GuidanceList/GuidanceList.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';

const { authData } = storeToRefs(useAppAuthData());
const { hasEditAccess } = storeToRefs(useCurrentJobProfile());
const { checkRbacPermissions } = useAppAuthData();

const {
  isTemplate,
  isHrisProfile,
  rescorePending,
  jobProfile,
  profileGuidanceMessages,
  fields,
  readOnly,
} = storeToRefs(useCurrentJobProfile());

const { setModal } = useModals();

const DRAFT_PREVIEW_COUNT = 2;
const draftsExpanded = ref(false);

const visibleDraftProfiles = computed(() => {
  const profiles = jobProfile.value?.draft_profiles ?? [];
  return draftsExpanded.value ? profiles : profiles.slice(0, DRAFT_PREVIEW_COUNT);
});

const hiddenDraftCount = computed(() => {
  const total = jobProfile.value?.draft_profiles?.length ?? 0;
  return Math.max(0, total - DRAFT_PREVIEW_COUNT);
});

const { isChromeView } = storeToRefs(useAppRoute());

const generateSummary = () => {
  setModal({ field: 'generateSummaryConfirm', value: true });
};

const generateButtonClick = (generatorType) => {
  const field = fields.value.find((f) => f.content_generator_type === generatorType);
  if (!field) return;

  setModal({
    field: 'generateFieldContentConfirm',
    value: true,
    params: { fieldTitle: field.label, generatorType },
  });
};
</script>
